const PERSISTED_STATUS_BY_UI_VALUE = {
  Awaiting: 'awaiting',
  Scanned: 'scanned',
  Cancelled: 'cancelled',
  ServiceIssue: 'serviceissue',
  Pick_In_Progress: 'pick_in_progress',
  PickInProgress: 'pick_in_progress',
  Picked: 'picked',
  Backordered: 'backordered'
};

const DERIVED_VIEW_BY_UI_VALUE = {
  ScannedToday: 'scanned_today',
  PartiallyScanned: 'partially_scanned',
  OnHold: 'action_required'
};

const FILTER_FIELD_BY_GRID_NAME = {
  OrderNumber: 'orderNumber',
  Store: 'store',
  Notes: 'notes',
  OrderDate: 'orderDate',
  Items: 'items',
  User: 'assignedUser',
  Status: 'status',
  Tags: 'tags',
  Skus: 'skus',
  Recipient: 'recipient',
  OSLMT: 'oslmt',
  customFieldOne: 'customFieldOne',
  customFieldTwo: 'customFieldTwo',
  trackingNumber: 'trackingNumber',
  country: 'country',
  city: 'city',
  email: 'email',
  tote: 'tote'
};

const normalizeList = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.includes(',')) {
    return value.split(',').map(item => item.trim()).filter(Boolean);
  }
  if (value === null || value === undefined || value === '') return [];
  return [value];
};

const toLegacyList = value => normalizeList(value).join(',');

const toArrayParams = (key, values) =>
  normalizeList(values)
    .map(value => `${key}[]=${encodeURIComponent(value)}`)
    .join('&');

export const normalizeOrderGridStatus = status =>
  PERSISTED_STATUS_BY_UI_VALUE[status] || status?.toString().toLowerCase();

export const normalizeOrderGridDerivedView = view =>
  DERIVED_VIEW_BY_UI_VALUE[view] || null;

export const splitOrderGridStates = states => {
  return normalizeList(states).reduce(
    (result, state) => {
      if (state?.toString().toLowerCase() === 'all') {
        return result;
      }
      const derivedView = normalizeOrderGridDerivedView(state);
      if (derivedView) {
        result.derivedViews.push(derivedView);
      } else {
        result.statuses.push(normalizeOrderGridStatus(state));
      }
      return result;
    },
    {statuses: [], derivedViews: []}
  );
};

const getDateWindowMode = dateValue => {
  if (dateValue === 'all_day') return 'all_retained';
  if (dateValue === 'start_date' || dateValue === 'end_date') return 'range';
  return 'last_days';
};

const buildDateWindow = (dateValue, dateRange) => {
  const mode = getDateWindowMode(dateValue);
  const range = dateRange || {start_date: '', end_date: ''};

  if (mode === 'range') {
    return {
      mode,
      startDate: range.start_date || '',
      endDate: range.end_date || '',
      legacyValue: dateValue || '',
      legacyRange: range
    };
  }

  if (mode === 'all_retained') {
    return {
      mode,
      days: null,
      legacyValue: dateValue || '',
      legacyRange: range
    };
  }

  return {
    mode,
    days: Number(dateValue) || null,
    legacyValue: dateValue || '',
    legacyRange: range
  };
};

const hasFilterValue = value => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') {
    return Object.keys(value).length > 0;
  }
  return value !== null && value !== undefined && value !== '';
};

const normalizeGridFilters = filterValue => {
  return normalizeList(filterValue)
    .filter(filter => filter && filter.name && hasFilterValue(filter.value))
    .map(filter => ({
      field: FILTER_FIELD_BY_GRID_NAME[filter.name] || filter.name,
      legacyName: filter.name,
      operator: filter.operator || 'eq',
      type: filter.type || 'string',
      value: filter.value
    }));
};

const getFilterValue = (filterValue, legacyName) => {
  const filter = normalizeList(filterValue).find(item => item?.name === legacyName);
  return filter?.value;
};

const buildFacetFilter = ({values, include, matchOption}) => ({
  values: normalizeList(values),
  mode: include === false ? 'exclude' : 'include',
  match: matchOption === 'All' ? 'all' : 'any'
});

export const buildOrderGridQueryContract = ({
  dateValue,
  dateRange,
  filterValue,
  rawFilterValue,
  matchOption,
  search,
  selectedStates,
  selectedTags,
  selectedSkus,
  selectedUsers,
  filterIncludedTags,
  filterIncludedSkus,
  sortField,
  sortOrder,
  skip,
  limit
}) => {
  const {statuses, derivedViews} = splitOrderGridStates(selectedStates);
  const canonicalFilters = normalizeGridFilters(rawFilterValue || filterValue);

  return {
    version: 1,
    dateWindow: buildDateWindow(dateValue, dateRange),
    statuses,
    derivedViews,
    search: {
      text: search || '',
      includeProductFields: false
    },
    filters: {
      columns: canonicalFilters,
      orderNumber: getFilterValue(rawFilterValue || filterValue, 'OrderNumber') || null,
      store: getFilterValue(rawFilterValue || filterValue, 'Store') || null,
      notes: getFilterValue(rawFilterValue || filterValue, 'Notes') || null,
      recipient: getFilterValue(rawFilterValue || filterValue, 'Recipient') || null,
      oslmt: getFilterValue(rawFilterValue || filterValue, 'OSLMT') || null,
      orderDate: getFilterValue(rawFilterValue || filterValue, 'OrderDate') || null,
      tags: buildFacetFilter({
        values: selectedTags,
        include: filterIncludedTags,
        matchOption
      }),
      skus: buildFacetFilter({
        values: selectedSkus,
        include: filterIncludedSkus,
        matchOption
      }),
      assignedUsers: normalizeList(selectedUsers)
    },
    sort: {field: sortField || '', direction: sortOrder || 'ASC'},
    pagination: {limit, offset: skip},
    legacy: {
      filter: toLegacyList(selectedStates) || 'all',
      filters: filterValue || [],
      matchOption: matchOption || 'Any',
      tagsName: toLegacyList(selectedTags),
      skusName: toLegacyList(selectedSkus),
      username: toLegacyList(selectedUsers)
    }
  };
};

export const buildLegacyOrderGridSearchParams = ({
  filter,
  sortOrder,
  limit,
  sortField,
  skip,
  dateValue,
  dateRange,
  filterValue,
  matchOption,
  search,
  filterIncludedTags,
  selectedTags,
  selectedUsers,
  filterIncludedSkus,
  selectedSkus,
  queryContract
}) => {
  const legacyParams = [
    `filter=${toLegacyList(filter) || 'all'}`,
    `order=${sortOrder}`,
    `limit=${limit}`,
    `sort=${sortField || ''}`,
    `offset=${skip}`,
    `app=${true}`,
    `count=${''}`,
    `dateValue=${dateValue || ''}`,
    `dateRange=${encodeURIComponent(JSON.stringify(dateRange || {start_date: '', end_date: ''}))}`,
    `filters=${encodeURIComponent(JSON.stringify(filterValue || []))}`,
    `match_option=${matchOption || 'Any'}`,
    `search=${encodeURIComponent(search || '')}`,
    `filterIncludedTags=${filterIncludedTags}`,
    `tags_name=${toLegacyList(selectedTags)}`,
    `username=${toLegacyList(selectedUsers)}`,
    `filterIncludedSkus=${filterIncludedSkus}`,
    toArrayParams('skus_name', selectedSkus)
  ];

  if (queryContract) {
    legacyParams.push(
      `canonical_query=${encodeURIComponent(JSON.stringify(queryContract))}`
    );
  }

  return legacyParams.filter(Boolean).join('&');
};
